import { createApp } from 'vue';
import '@/tailwind-style.css';
import '@/style.sass';
import App from './App.vue';
import { createI18n } from 'vue-i18n';
import en from '@/lang/en.json';
import fr from '@/lang/fr.json';
import nl from '@/lang/nl.json';
import fr_LU from '@/lang/fr_LU.json';
import en_LU from '@/lang/en_LU.json';
import de_LU from '@/lang/de_LU.json';
import router from './router';
import { createPinia } from 'pinia';
import { createHead } from '@unhead/vue/client';
import * as Sentry from '@sentry/vue';
import { useFavicon } from '@vueuse/core';
import { useAuthStore } from '@/stores/auth.ts';
import { install } from 'vue3-recaptcha-v2';

import enComponents from '@monizze/monizze-components/lang/en';
import frComponents from '@monizze/monizze-components/lang/fr';
import nlComponents from '@monizze/monizze-components/lang/nl';

const head = createHead();
const app = createApp(App);
app.use(router);
app.use(head);

const pinia = createPinia();
app.use(pinia);

app.use(install, {
	sitekey: import.meta.env.VITE_CAPTCHA_SITEKEY,
	cnDomains: false,
});

// Pre-detect locale
const isLux: boolean = useAuthStore().isLux;
const userLocale: string = (navigator.language || '').split('-')[0];
export const detectedLanguage: string = isLux
	? ['fr', 'en'].includes(userLocale)
		? userLocale
		: 'fr'
	: ['en', 'fr', 'nl'].includes(userLocale)
		? userLocale
		: 'en';

useFavicon('/favicon.png');

// Register only the platform-relevant locales so MzLang's picker shows the right set.
// _LU locales inherit from their base (fr/en) via object merge to preserve keys that
// previously resolved through `fallbackLocale: 'fr'`.
const messages = isLux
	? {
			fr_LU: { ...frComponents, ...fr, ...fr_LU },
			en_LU: { ...enComponents, ...en, ...en_LU },
			de_LU: { ...frComponents, ...fr, ...de_LU },
		}
	: {
			en: { ...enComponents, ...en },
			fr: { ...frComponents, ...fr },
			nl: { ...nlComponents, ...nl },
		};

const i18n = createI18n({
	legacy: false,
	locale: isLux ? detectedLanguage + '_LU' : detectedLanguage,
	fallbackLocale: isLux ? 'fr_LU' : 'en',
	messages: messages as never,
});
app.use(i18n);

Sentry.init({
	app,
	dsn: import.meta.env.VITE_SENTRY_DSN,
	integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
	// Performance Monitoring
	tracesSampleRate: 0.0,
	tracePropagationTargets: [
		'localhost',
		/^https:\/\/account-api\.dev\.monizze\.be/,
		/^https:\/\/account-api\.staging\.monizze\.be/,
		/^https:\/\/account-api\.monizze\.be/,
	],
	// Session Replay
	replaysSessionSampleRate: 0.0,
	replaysOnErrorSampleRate: 0.0,
});

app.mount('#app');
